import { toColor, type Color, type EntityGroupItem, type IconName, type IconSource } from "@salestrak/ui";
import type { EntityRefValue } from "#app/lib/data";
import type { WorkspaceDataState } from "#app/lib/data/workspace-data-provider";
import { isInvited, isSuspended } from "#app/lib/role-utils";
import { pluralize } from "#app/lib/utils/pluralize";
import type { Omittable } from "@salestrak/ui";

// Canonical source for entity kind → icon/label/visual resolution. The
// kind-fallback icon literals live ONLY here — never hard-code them elsewhere.

export type EntityKind =
  | "user"
  | "team"
  | "stage"
  | "workflow"
  | "issue"
  | "issueType"
  | "issueSource";

// Fallbacks only — the entity's own `icon{icon,color}` always wins. Users
// render avatars and stages render the pie glyph, so neither needs a fallback.
export const ENTITY_KIND_ICON: Record<
  Exclude<EntityKind, "stage">,
  IconName
> = {
  user: "user",
  team: "users",
  workflow: "git-branch",
  issue: "circle-dot",
  issueType: "package",
  issueSource: "tag",
};

export function iconSourceToName(
  icon: IconSource | null | undefined,
): IconName | null {
  return typeof icon === "string" ? icon : null;
}

// Structural supersets of the codegen fragments (WorkspaceUser, WorkspaceTeam,
// WorkflowStageFragment, …) which satisfy them structurally — not local duplicates.

export type EntityUserData = Omittable<{
  name?: string | null;
  email?: string | null;
  username?: string | null;
  profileImage?: string | null;
  memberStatus?: string | null;
}>;

export type EntityTeamData = Omittable<{
  teamName: string;
  teamKey?: string | null;
  icon?: Omittable<{ icon?: string | null; color?: string | null }> | null;
}>;

export type EntityStageData = Omittable<{
  name?: string | null;
  type?: string | null;
  icon?: Omittable<{ color?: string | null; percentage?: number | null }> | null;
}>;

export type EntityIconData = Omittable<{
  name?: string | null;
  icon?: Omittable<{ icon?: string | null; color?: string | null }> | null;
}>;

// Call sites build these inline as a component prop (`entity={{ kind: "stage",
// id, stage, workflowId }}`), and `workflowId` is routinely a nullable field
// read straight off a fragment — so an absent key and an explicit `undefined`
// are the same entity, and the two members carrying one relax.
export type EntityData =
  | { kind: "user"; id: string; user: EntityUserData | null }
  | { kind: "team"; id: string; team: EntityTeamData }
  | Omittable<{
      kind: "stage";
      id: string;
      stage: EntityStageData;
      workflowId?: string;
    }>
  | { kind: "workflow"; id: string; workflow: EntityIconData }
  | { kind: "issueType"; id: string; issueType: EntityIconData }
  | Omittable<{
      kind: "issue";
      id: string;
      issue: EntityIconData;
      workflowId?: string;
    }>
  | { kind: "issueSource"; id: string; issueSource: EntityIconData };

/**
 * Structural equality for two `EntityData` values.
 *
 * Call sites build the entity inline (`entity={{ kind: "stage", id, stage }}`),
 * so a fresh object arrives every render and reference equality never holds —
 * memoized chips have to compare by value. `EntityData` is a closed union whose
 * payloads are one object of primitives, at most with a nested `icon`, so a
 * two-level walk is exact. It replaces a `JSON.stringify` pair that ran per chip
 * per render (serializing to compare is O(size) with allocation, and orders of
 * magnitude slower than the handful of field reads below).
 */
export function entityDataEqual(
  a: EntityData | null | undefined,
  b: EntityData | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  // The discriminator and id reject almost every mismatch before any payload
  // walk — a chip whose entity changed at all nearly always changed its id.
  if (a.kind !== b.kind || a.id !== b.id) return false;
  if (
    (a as { workflowId?: string }).workflowId !==
    (b as { workflowId?: string }).workflowId
  ) {
    return false;
  }
  return payloadEqual(
    (a as unknown as Record<string, unknown>)[a.kind],
    (b as unknown as Record<string, unknown>)[b.kind],
  );
}

/** One level of object walk, with `icon` (the only nested object) recursed. */
function payloadEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  if (aKeys.length !== Object.keys(bRecord).length) return false;
  for (const key of aKeys) {
    const aValue = aRecord[key];
    const bValue = bRecord[key];
    if (aValue === bValue) continue;
    if (
      typeof aValue === "object" &&
      typeof bValue === "object" &&
      aValue !== null &&
      bValue !== null
    ) {
      if (!payloadEqual(aValue, bValue)) return false;
      continue;
    }
    return false;
  }
  return true;
}

const TYPENAME_TO_KIND: Record<string, EntityKind> = {
  User: "user",
  Member: "user",
  Team: "team",
  Stage: "stage",
  Workflow: "workflow",
  Issue: "issue",
  IssueType: "issueType",
  IssueSource: "issueSource",
};

export function entityKindFromTypename(t: string): EntityKind | undefined {
  return TYPENAME_TO_KIND[t];
}

// Assignees are members-only — an assignee ref is always a user.
export function entityFromAssigneeRef(ref: EntityRefValue): EntityData {
  return { kind: "user", id: ref.entityId, user: ref.user ?? null };
}

/**
 * Resolve a view's data-scope ref (`scopeType` + `scopeId`) into a navigable
 * entity. A view's `scopeType` is the route segment it lives on (`workflow` /
 * `team` / `member`); this is the ONE place that maps those segments to entity
 * kinds, so the generic view kit never hard-codes a domain `case "workflow"`.
 * Returns `null` for a roster scope (no `scopeId`) or an entity absent from the
 * workspace maps — the caller falls back to a label.
 */
export function entityFromScopeRef(
  scopeType: string,
  scopeId: string,
  maps: Pick<WorkspaceDataState, "workflowMap" | "teamMap" | "userMap">,
): EntityData | null {
  switch (scopeType) {
    case "workflow": {
      const workflow = maps.workflowMap.get(scopeId);
      return workflow ? { kind: "workflow", id: scopeId, workflow } : null;
    }
    case "team": {
      const team = maps.teamMap.get(scopeId);
      return team ? { kind: "team", id: scopeId, team } : null;
    }
    case "member": {
      const user = maps.userMap.get(scopeId);
      return user ? { kind: "user", id: scopeId, user } : null;
    }
    default:
      return null;
  }
}

/**
 * The `EntityData` behind a member-OR-team id — the shape an assignee, a
 * recipient, or any other "who" field on an automation holds, where the same
 * column can carry either kind. Null when neither map knows the id.
 */
export function entityFromMemberOrTeam(
  maps: Pick<WorkspaceDataState, "userMap" | "teamMap">,
  id: string,
): EntityData | null {
  const user = maps.userMap.get(id);
  if (user) return { kind: "user", id, user };
  const team = maps.teamMap.get(id);
  return team ? { kind: "team", id, team } : null;
}

/**
 * That id's display label, or null when it resolves to nothing.
 *
 * Goes through `resolveEntityLabel` rather than reading `.name`, and that is the
 * whole point: a member's `name` is legitimately empty until they onboard — an
 * invited account has only an email — so `.name` alone yields null and every
 * caller then prints its raw fallback. That is how a user id ("2FQDFgCdMNYbmc")
 * reached the automation node captions and sentences. Use this for any surface
 * that WORDS a who-field; they are all supposed to read identically.
 */
export function resolveMemberOrTeamLabel(
  maps: Pick<WorkspaceDataState, "userMap" | "teamMap">,
  id: string,
): string | null {
  const entity = entityFromMemberOrTeam(maps, id);
  return entity ? resolveEntityLabel(entity) : null;
}

export function userRefToEntity(
  user: { userId: string } & EntityUserData,
): EntityData {
  const { userId, ...rest } = user;
  return { kind: "user", id: userId, user: rest };
}

export function teamRefToEntity(
  team: { teamId: string } & EntityTeamData,
): EntityData {
  const { teamId, ...rest } = team;
  return { kind: "team", id: teamId, team: rest };
}

export function resolveEntityLabel(e: EntityData): string {
  switch (e.kind) {
    case "user":
      return e.user?.name || e.user?.email || e.user?.username || "?";
    case "team":
      return e.team.teamName || "?";
    case "stage":
      return e.stage.name ?? "";
    case "workflow":
      return e.workflow.name ?? "";
    case "issueType":
      return e.issueType.name ?? "";
    case "issue":
      return e.issue.name?.trim() || e.id;
    case "issueSource":
      return e.issueSource.name ?? "";
  }
}

// A stage has no glyph slot in the entity-group primitive (no
// `ENTITY_KIND_ICON.stage`); don't put stages in a group, render a single one.
export function entityToGroupItem(entity: EntityData): EntityGroupItem {
  const visual = resolveEntityVisual(entity);
  if (visual.type === "avatar") {
    return {
      id: entity.id,
      name: visual.label,
      imageUrl: visual.user?.profileImage ?? null,
      muted: visual.suspended,
      invited: visual.invited,
    };
  }
  return {
    id: entity.id,
    name: resolveEntityLabel(entity),
    icon: visual.type === "icon" ? visual.icon : null,
    iconColor: visual.type === "icon" ? (visual.color ?? null) : null,
  };
}

// Returns null for an empty type so callers decide their own empty text.
export function stageTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  const spaced = type.replace(/([A-Z])/g, " $1");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// `amount` of 0 is a legitimate SLA ("same day"), so guard on `null`, not falsy.
export function formatSla(
  age:
    | { onTrack?: { amount?: number | null; step?: string | null } | null }
    | null
    | undefined,
): string | null {
  const amount = age?.onTrack?.amount;
  const step = age?.onTrack?.step;
  if (amount == null || !step) return null;
  return `${amount} ${pluralize(step, { count: amount })}`;
}

export function memberStatusBadge(status: string | null | undefined): {
  label: string;
  color: "success" | "warning" | "muted";
} {
  const normalized = status?.toLowerCase();
  if (normalized === "active") return { label: "Active", color: "success" };
  if (normalized === "invited") return { label: "Invited", color: "warning" };
  if (!status) return { label: "Unknown", color: "muted" };
  return {
    label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
    color: "muted",
  };
}

// Flat glyph primitives for render targets that cannot host React components
// (Google Maps SVG pins, canvas). Icon kinds get the kind-fallback icon; a
// stage carries only its color (the pie glyph is React-only); users carry
// neither (they render avatars). Everything React renders through
// EntityTag/EntityVisual/EntityGroupTag instead.
export function getEntityGlyph(e: EntityData): {
  icon: IconName | null;
  color: Color | null;
} {
  const visual = resolveEntityVisual(e);
  switch (visual.type) {
    case "icon":
      return { icon: visual.icon, color: visual.color ?? null };
    case "stage":
      return { icon: null, color: toColor(visual.stage.icon?.color) ?? null };
    case "avatar":
      return { icon: null, color: null };
  }
}

export type EntityVisual =
  | {
      type: "avatar";
      user: EntityUserData | null;
      label: string;
      suspended: boolean;
      invited: boolean;
    }
  | { type: "stage"; stage: EntityStageData }
  // An entity that stores no color has none; every reader already spells that
  // `visual.color ?? …`, so an absent key and an explicit `undefined` are the
  // same answer and the member relaxes rather than forcing `toColor()`'s
  // result to be split into "set the key" and "don't".
  | Omittable<{ type: "icon"; icon: IconName | null; color?: Color }>;

// INTERNAL to the entity system — only `components/entity/*` and this file may
// import it. Feature code renders entities through EntityTag/EntityVisual/
// EntityGroupTag, or uses getEntityGlyph for non-React glyph strings.
export function resolveEntityVisual(e: EntityData): EntityVisual {
  switch (e.kind) {
    case "user":
      return {
        type: "avatar",
        user: e.user,
        label: resolveEntityLabel(e),
        suspended: isSuspended(e.user),
        invited: isInvited(e.user),
      };
    case "stage":
      return { type: "stage", stage: e.stage };
    case "team":
      return {
        type: "icon",
        icon: (e.team.icon?.icon as IconName) ?? ENTITY_KIND_ICON.team,
        color: toColor(e.team.icon?.color),
      };
    case "workflow":
      return {
        type: "icon",
        icon: (e.workflow.icon?.icon as IconName) ?? ENTITY_KIND_ICON.workflow,
        color: toColor(e.workflow.icon?.color),
      };
    case "issueType":
      return {
        type: "icon",
        icon:
          (e.issueType.icon?.icon as IconName) ?? ENTITY_KIND_ICON.issueType,
        color: toColor(e.issueType.icon?.color),
      };
    case "issue":
      return {
        type: "icon",
        icon: (e.issue.icon?.icon as IconName) ?? ENTITY_KIND_ICON.issue,
        color: toColor(e.issue.icon?.color),
      };
    case "issueSource":
      // Sources have no glyph — when no icon is supplied the visual is empty
      // and only the label renders.
      return {
        type: "icon",
        icon: (e.issueSource.icon?.icon as IconName) ?? null,
        color: toColor(e.issueSource.icon?.color),
      };
  }
}
